// ===============================================
// 🌐 БЭКЕНД API СЕРВИС (ФРОНТЕНД)
// ===============================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const VISORIA_BACKEND_URL =
  process.env.REACT_APP_VISORIA_BACKEND_URL || BACKEND_URL;

class BackendApiService {
  constructor() {
    this.baseURL = BACKEND_URL;
    this.visoriaBaseURL = VISORIA_BACKEND_URL;
  }

  async makeRequest(endpoint, options = {}, baseURL = this.baseURL) {
    const url = `${baseURL}${endpoint}`;

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            message = errorBody.error;
          } else if (errorBody?.message) {
            message = errorBody.message;
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ===============================================
  // 🎮 STEAM API
  // ===============================================

  // Проверка Steam аккаунта
  async verifySteamAccount(account) {
    return this.makeRequest('/api/steam/verify-account', {
      method: 'POST',
      body: JSON.stringify({ account })
    });
  }

  // Создание платежа Steam
  async createSteamPayment(account, amount, currency = 'USD') {
    return this.makeRequest('/api/steam/create-payment', {
      method: 'POST',
      body: JSON.stringify({ account, amount, currency })
    });
  }

  // Пополнение Steam аккаунта
  async topupSteamAccount(account, amount, paymentId) {
    return this.makeRequest('/api/steam/topup-account', {
      method: 'POST',
      body: JSON.stringify({ account, amount, paymentId })
    });
  }

  // ===============================================
  // 💳 СБП API
  // ===============================================

  // Создание QR-кода СБП
  async createSBPQR(amount, steamAccount) {
    return this.makeRequest('/api/sbp/create-qr', {
      method: 'POST',
      body: JSON.stringify({ amount, steamAccount })
    });
  }

  // Создание криптоплатежа
  async createCryptoPayment(amountUSD, steamAccount) {
    return this.makeRequest('/api/sbp/create-crypto-payment', {
      method: 'POST',
      body: JSON.stringify({ amountUSD, steamAccount })
    });
  }

  async getCryptoCommission() {
    return this.makeRequest('/api/currency/crypto-commission', {
      method: 'GET'
    });
  }


  // Проверка статуса платежа СБП
  async checkSBPPaymentStatus(paymentGuid) {
    return this.makeRequest(`/api/sbp/payment-status/${paymentGuid}`, {
      method: 'GET'
    });
  }


  // ===============================================
  // 💱 КУРСЫ ВАЛЮТ API
  // ===============================================

  // Получение курсов валют
  async getCurrencyRates() {
    return this.makeRequest('/api/currency/rates', {
      method: 'GET'
    });
  }

  // Конвертация валют
  async convertCurrency(amount, fromCurrency, toCurrency) {
    return this.makeRequest('/api/currency/convert', {
      method: 'POST',
      body: JSON.stringify({ amount, fromCurrency, toCurrency })
    });
  }

  // Получение информации о валютах
  async getCurrencyInfo() {
    return this.makeRequest('/api/currency/info', {
      method: 'GET'
    });
  }

  // ===============================================
  // 🔗 WEBHOOK
  // ===============================================

  // Проверка статуса webhook
  async checkWebhookStatus() {
    return this.makeRequest('/webhook/sbp', {
      method: 'GET'
    });
  }

  // ===============================================
  // 📋 ЗАКАЗЫ
  // ===============================================

  // Создание заказа
  async createOrder(steamAccount, amountUSD, description) {
    return this.makeRequest('/api/orders/create', {
      method: 'POST',
      body: JSON.stringify({ steamAccount, amountUSD, description })
    });
  }

  // Получение статуса заказа
  async getOrderStatus(orderId) {
    return this.makeRequest(`/api/orders/status/${orderId}`, {
      method: 'GET'
    });
  }


  // ===============================================
  // 🏥 HEALTH CHECK
  // ===============================================

  // Проверка здоровья сервера
  async healthCheck() {
    return this.makeRequest('/health', {
      method: 'GET'
    });
  }

  // ===============================================
  // 🎁 VISORIA — другие магазины (через бэкенд)
  // ===============================================

  async getOtherStoreList() {
    return this.makeRequest('/api/visoria', { method: 'GET' }, this.visoriaBaseURL);
  }

  async getOtherStore(storeId) {
    return this.makeRequest(
      `/api/visoria/${encodeURIComponent(storeId)}`,
      { method: 'GET' },
      this.visoriaBaseURL,
    );
  }

  async getOtherStoreUiConfig() {
    return this.makeRequest(
      '/api/visoria/config/ui',
      { method: 'GET' },
      this.visoriaBaseURL,
    );
  }

  async trackReferralVisit(refCode) {
    const url = `${this.visoriaBaseURL}/api/visoria/referral/visit`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refCode }),
    });
    const body = await response.json();
    if (!response.ok || !body.success) {
      throw new Error(body.error || `HTTP error! status: ${response.status}`);
    }
    return body;
  }

  async checkoutOtherStore(storeId, payload) {
    const url = `${this.visoriaBaseURL}/api/visoria/${encodeURIComponent(storeId)}/checkout`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    let body = {};
    try {
      body = await response.json();
    } catch {
      body = {};
    }

    if (!response.ok) {
      const error = new Error(body.error || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.code = body.code;
      error.amountRub = body.amountRub;
      error.unitRub = body.unitRub;
      error.amountUsdt = body.amountUsdt;
      error.saleUsd = body.saleUsd;
      throw error;
    }

    return body;
  }

  async getOtherStoreOrder(orderId, { accessToken, email } = {}) {
    const params = new URLSearchParams();
    if (accessToken) {
      params.set("token", accessToken);
    }
    if (email) {
      params.set("email", email);
    }
    const query = params.toString();
    return this.makeRequest(
      `/api/visoria/orders/${encodeURIComponent(orderId)}${query ? `?${query}` : ""}`,
      { method: 'GET' },
      this.visoriaBaseURL,
    );
  }
}

// Создаем единственный экземпляр сервиса
const backendApiService = new BackendApiService();

export default backendApiService;
